// Store Item Inventory main entity
export interface StoreItemInventory {
  id: number;                           // 主键，自增
  storeCode: string;                    // 门店编码
  ownerCode: string;                    // 货主编码
  itemCode: string;                     // 货品编码
  quantity: number;                     // 库存数量
  creatorAccount: string;               // 创建人账号
  creatorName: string;                  // 创建人姓名
  gmtCreate: number;                    // 创建时间戳
  modifierAccount?: string;             // 修改人账号，可空
  modifierName?: string;                // 修改人姓名，可空
  gmtModified: number;                  // 修改时间戳
  deleted: number;                      // 逻辑删除标记 (tinyint: 0/1)
  
  // 关联数据（用于显示）
  storeName?: string;                   // 门店名称
  itemName?: string;                    // 货品名称
}

// Store entity for dropdown (reusing from item-store-relationship)
export interface Store {
  code: string;                         // 门店代码
  name: string;                         // 门店名称
  type?: string;                        // 门店类型
  status?: string;                      // 门店状态
}

// Item entity for dropdown (reusing from item-store-relationship)
export interface Item {
  code: string;                         // 货品代码
  name: string;                         // 货品名称
  ownerCode?: string;                   // 货主代码
  category?: string;                    // 货品分类
  status?: string;                      // 货品状态
}

// Filter criteria for store item inventory list
export interface StoreItemInventoryFilters {
  storeCode?: string;                   // 门店编码筛选
  itemCode?: string;                    // 货品编码筛选
  ownerCode?: string;                   // 货主编码筛选
  search?: string;                      // 搜索关键词（可搜索门店编码、货品编码等）
}

// Pagination parameters
export interface StoreItemInventoryPaginationParams {
  currentPage: number;
  pageSize: number;
  sortBy?: StoreItemInventorySortField;
  sortOrder?: SortOrder;
}

// API Response wrapper following backend standards
export interface ApiResponse<T> {
  traceId: string;
  success: boolean;
  msg?: string | null;
  errorCode?: string | null;
  errorMessage: string | null;
  flag: 'success' | 'fail';
  code?: string | null;
  message?: string | null;
  data: T | null;
}

// Paginated response for store item inventory lists following backend standards
export interface StoreItemInventoryPaginatedResponse<T> {
  tableData: T[];
  paging: {
    currentPage: number;
    totalCount: number;
    pageSize: number;
  };
}

// Sort options for store item inventory table
export type StoreItemInventorySortField =
  | 'id'
  | 'storeCode'
  | 'itemCode'
  | 'ownerCode'
  | 'quantity'
  | 'gmtCreate'
  | 'gmtModified'
  | 'creatorName'
  | 'modifierName';

export type SortOrder = 'asc' | 'desc';

// Table column keys for store item inventory table
export type StoreItemInventoryTableColumnKey =
  | 'id'
  | 'storeCode'
  | 'storeName'
  | 'itemCode'
  | 'itemName'
  | 'ownerCode'
  | 'quantity'
  | 'creatorName'
  | 'gmtCreate'
  | 'modifierName'
  | 'gmtModified';

// Error types for better error handling
export interface StoreItemInventoryErrorInfo {
  type: 'network' | 'server' | 'permission' | 'notFound' | 'validation' | 'business';
  message: string;
  code?: string;
  traceId?: string;
}

// Loading states for store item inventory management
export interface StoreItemInventoryLoadingState {
  list: boolean;
  storeOptions: boolean;
  itemOptions: boolean;
}

// Component props interfaces
export interface StoreItemInventoryFilterPanelProps {
  filters: StoreItemInventoryFilters;
  onFiltersChange: (filters: StoreItemInventoryFilters) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  storeOptions: Store[];
  itemOptions: Item[];
  onStoreSearch: (searchValue: string) => void;
  onItemSearch: (searchValue: string) => void;
}

export interface StoreItemInventoryTableProps {
  data: StoreItemInventory[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
  };
  loading: boolean;
  onPageChange: (page: number, pageSize: number) => void;
  onSortChange: (field: StoreItemInventoryTableColumnKey, order: SortOrder) => void;
  onRefresh: () => void;
}

export interface StoreItemInventoryListProps {
  // No props needed - self-contained component
}

// Quantity display formatting
export interface QuantityDisplayConfig {
  decimalPlaces: number;                // 小数位数
  thousandSeparator: boolean;           // 是否显示千分位分隔符
  unit?: string;                        // 数量单位
}

// Constants for quantity formatting
export const DEFAULT_QUANTITY_CONFIG: QuantityDisplayConfig = {
  decimalPlaces: 4,
  thousandSeparator: true,
  unit: undefined
};